import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 8-hour session timeout (in milliseconds)
const SESSION_TIMEOUT = 8 * 60 * 60 * 1000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inactivityTimer = useRef(null);
  const lastActivity = useRef(Date.now());

  // Reset inactivity timer on user activity
  const resetInactivityTimer = useCallback(() => {
    lastActivity.current = Date.now();
    localStorage.setItem("last_activity", lastActivity.current.toString());
  }, []);

  // Logout function
  const logout = useCallback(() => {
    if (inactivityTimer.current) {
      clearInterval(inactivityTimer.current);
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("last_activity");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  }, []);

  // Check for session timeout
  const checkSessionTimeout = useCallback(() => {
    const storedLastActivity = localStorage.getItem("last_activity");
    const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity) : Date.now();
    const timeSinceActivity = Date.now() - lastActivityTime;
    
    if (timeSinceActivity >= SESSION_TIMEOUT) {
      logout();
      return true;
    }
    return false;
  }, [logout]);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        // Check if session has timed out
        if (checkSessionTimeout()) {
          setLoading(false);
          return;
        }
        
        try {
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          const res = await axios.get(`${API}/auth/me`);
          setUser(res.data);
          resetInactivityTimer();
        } catch (err) {
          // Token invalid or expired
          localStorage.removeItem("auth_token");
          localStorage.removeItem("last_activity");
          delete axios.defaults.headers.common["Authorization"];
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [checkSessionTimeout, resetInactivityTimer]);

  // Set up activity listeners for session timeout
  useEffect(() => {
    if (!user) return;

    // Activity events to track
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
    
    // Throttled activity handler (update at most once per minute)
    let lastUpdate = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > 60000) {
        lastUpdate = now;
        resetInactivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check for timeout every minute
    inactivityTimer.current = setInterval(() => {
      checkSessionTimeout();
    }, 60000);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimer.current) {
        clearInterval(inactivityTimer.current);
      }
    };
  }, [user, resetInactivityTimer, checkSessionTimeout]);

  // Refresh token periodically to extend session (only if active)
  useEffect(() => {
    if (!user) return;
    
    const refreshInterval = setInterval(async () => {
      // Only refresh if session hasn't timed out
      if (checkSessionTimeout()) return;
      
      try {
        const res = await axios.post(`${API}/auth/refresh`);
        localStorage.setItem("auth_token", res.data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
      } catch (err) {
        // Session expired
        logout();
      }
    }, 30 * 60 * 1000); // Refresh every 30 minutes

    return () => clearInterval(refreshInterval);
  }, [user, logout, checkSessionTimeout]);

  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password });
      const { token, user: userData } = res.data;
      
      localStorage.setItem("auth_token", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(userData);
      resetInactivityTimer();
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed";
      setError(message);
      return { success: false, error: message };
    }
  }, [resetInactivityTimer]);

  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error, 
      login, 
      logout,
      isAuthenticated: !!user,
      isAdmin,
      isStaff
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
